import React from "react";
import { ViewKey } from "../../v2/components/ViewKey.jsx";
import { CAPACITY_CELL, SEAT_STYLE, INK, statusPillStyle } from "./capacityStyles.js";
import { LONG_RANGE_NOTE } from "./capacityModel.js";

const cell = (k) => ({ kind: "cell", color: CAPACITY_CELL[k].bg, border: CAPACITY_CELL[k].border, borderStyle: CAPACITY_CELL[k].dashed ? "dashed" : "solid" });
const seat = (k) => {
  const [, borderStyle, ...color] = SEAT_STYLE[k].border.split(" ");
  return { kind: "cell", color: SEAT_STYLE[k].background, border: color.join(" "), borderStyle };
};

// The single Capacity key configuration. Exported for tests.
export function capacityKeyConfig({ view = "monthly", bufferPct = 10 } = {}) {
  const thr = 100 - bufferPct;
  return {
    groups: [
      { label: "Status", columns: 2, items: [
        { label: "Comfortable", labelStyle: statusPillStyle("comfortable"), description: `under ${thr}% of usable seats (one tint, quiet on purpose)`, swatch: cell("comfortable") },
        { label: "Tight", labelStyle: statusPillStyle("tight"), description: `${thr}% up to and including 100% of usable seats`, swatch: cell("tight") },
        { label: "Densify", labelStyle: statusPillStyle("densify"), description: "above usable seats, up to the attendance-adjusted maximum", swatch: cell("densify") },
        { label: "Over", labelStyle: statusPillStyle("over"), description: "above the attendance-adjusted maximum, or above usable seats when there is no recorded headroom", swatch: cell("over") },
        { label: "No data", description: "no projected headcount, seats or attendance (empty square)", swatch: cell("no_data") },
        { label: "Under 3 months history", description: "trend not yet reliable", swatch: cell("history") },
      ] },
      { label: "Markers", items: [
        { label: "Committed change", description: "capacity schedule row that changes installed seats", swatch: { kind: "diamond", color: INK } },
        { label: "Changed by scenario", description: "1.5px outline where scenario status differs from today", swatch: { kind: "ring", color: INK, borderWidth: 1.5 } },
      ] },
      { label: "Seat grid", columns: 2, items: [
        { label: "Used", swatch: seat("used") },
        { label: "Spare", swatch: seat("spare") },
        { label: "Densify used", swatch: seat("densUsed") },
        { label: "Densify room", swatch: seat("densRoom") },
        { label: "Over", description: "1 square = 1 seat; 5 seats above 150 installed seats", swatch: seat("over") },
         { label: "Installed seats", description: "2px solid divider at installed seats, including inside a scaled square. If outside the represented range, disclosed in the summary.", swatch: { kind: "ring", color: INK, borderWidth: 2 } },
      ] },
    ],
    footer: `Seats = usable seats: installed seats adjusted for peak attendance. Maximum = recorded maximum × usable ÷ installed, same month; no Densify without recorded headroom (no maximum, or maximum equal to installed). ` + (view === "quarterly"
      ? `The same next 36 months, grouped into calendar quarters (12 or 13). Partial endpoint quarters include only months in the window, shown at their fullest month (worst status, then highest ratio). Includes committed changes. ${LONG_RANGE_NOTE} Faded squares (same colours, reduced opacity) are long-range.`
      : `Each square is one market, one month across the next 36 months: projected assigned headcount against usable seats. Includes committed changes. ${LONG_RANGE_NOTE} Months 25–36 are faded. Hover or focus a square for the numbers.`),
  };
}

export function CapacityViewKey({ view, bufferPct }) {
  const cfg = capacityKeyConfig({ view, bufferPct });
  return <ViewKey testId="capacity-key" title="How to read Capacity" groups={cfg.groups} footer={cfg.footer} />;
}
